import { subDays, format, parseISO } from "date-fns";
import type { InstallPeriodType, InstallRecordType, Granularity } from "@intelligentlilli/api-layer";
import theme from "../../Styles/theme.scss";

// --- Presets ---

export type PresetKey =
  | "last7days"
  | "last14days"
  | "last4weeks"
  | "currentMonth"
  | "lastMonth"
  | "last3months"
  | "last6months"
  | "last12months"
  | "last24months"
  | "last36months";


export interface Preset {
  key: PresetKey;
  label: string;
  defaultGranularity: Granularity;
  allowedGranularities: Granularity[];
  // Day presets span today + the previous N-1 days. Month presets span the
  // current calendar month + the previous N-1 months (a rolling day count can
  // never align with the chart's calendar-month buckets).
  days?: number;
  months?: number;
  // Anchors to a specific calendar month instead of a rolling window:
  // "current" = 1st of this month through now (partial); "previous" = the
  // full previous calendar month, 1st through its last moment.
  calendarMonth?: "current" | "previous";
}

export const PRESETS: Preset[] = [
  { key: "last7days", label: "Last 7 days", defaultGranularity: "D", allowedGranularities: ["D"], days: 7 },
  { key: "last14days", label: "Last 14 days", defaultGranularity: "D", allowedGranularities: ["D", "W"], days: 14 },
  { key: "last4weeks", label: "Last 4 weeks", defaultGranularity: "W", allowedGranularities: ["D", "W"], days: 31 },
  { key: "currentMonth", label: "Current calendar month", defaultGranularity: "D", allowedGranularities: ["D", "W"], calendarMonth: "current" },
  { key: "lastMonth", label: "Last calendar month", defaultGranularity: "D", allowedGranularities: ["D", "W"], calendarMonth: "previous" },
  { key: "last3months", label: "Last 3 months", defaultGranularity: "M", allowedGranularities: ["W", "M"], months: 3 },
  { key: "last6months", label: "Last 6 months", defaultGranularity: "M", allowedGranularities: ["M"], months: 6 },
  { key: "last12months", label: "Last 12 months", defaultGranularity: "M", allowedGranularities: ["M"], months: 12 },
  { key: "last24months", label: "Last 24 months", defaultGranularity: "M", allowedGranularities: ["M"], months: 24 },
  { key: "last36months", label: "Last 36 months", defaultGranularity: "M", allowedGranularities: ["M"], months: 36 },
];

export const PRESET_OPTIONS = PRESETS.map((p) => ({
  label: p.label,
  value: p.key,
}));

// --- Date range computation ---

// The API's is-utc-timestamp? validation requires UTC timestamps without
// milliseconds, e.g. "2026-08-13T14:22:31Z" (toISOString() emits ".000Z").
export const toUtcTimestamp = (date: Date): string =>
  date.toISOString().replace(/\.\d{3}Z$/, "Z");

export const getPresetDateRange = (
  presetKey: PresetKey
): { startTime: string; endTime: string } => {
  const now = new Date();
  const endTime = toUtcTimestamp(now);

  const preset = PRESETS.find((p) => p.key === presetKey);

  if (preset?.calendarMonth === "current") {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    return { startTime: toUtcTimestamp(start), endTime };
  }

  if (preset?.calendarMonth === "previous") {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    // One second before the start of this month = the last moment of the
    // previous one. Date.UTC normalises the negative month index.
    const end = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1) - 1000
    );
    return { startTime: toUtcTimestamp(start), endTime: toUtcTimestamp(end) };
  }

  // Month presets anchor to the first of the month N-1 months back so the
  // chart shows exactly N calendar-month buckets (the current partial month
  // plus the previous N-1). Date.UTC normalises negative month indices.
  if (preset?.months) {
    const start = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (preset.months - 1), 1)
    );
    return { startTime: toUtcTimestamp(start), endTime };
  }

  const days = preset?.days || 7;
  // "Last N days" means today plus the previous N-1 days, so subtract N-1.
  // Use UTC midnight (not the viewer's local startOfDay) so the chart's
  // org-timezone bucketing doesn't shift the start onto an extra earlier day.
  const start = subDays(now, days - 1);
  const startTime = toUtcTimestamp(
    new Date(
      Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate())
    )
  );

  return { startTime, endTime };
};

// --- Data transforms ---

export type InstallOutcome = "succeeded" | "failed";

export type InstallRecordWithOutcome = InstallRecordType & {
  outcome: InstallOutcome;
};

// Combined feed for the single installs table — every succeeded and failed
// record, tagged with its outcome, newest first.
export const flattenAllRecords = (
  periods: InstallPeriodType[]
): InstallRecordWithOutcome[] => {
  const succeeded = periods.flatMap((period) =>
    period.records.succeeded.map((r) => ({ ...r, outcome: "succeeded" as const }))
  );
  const failed = periods.flatMap((period) =>
    period.records.failed.map((r) => ({ ...r, outcome: "failed" as const }))
  );
  return [...succeeded, ...failed].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
};

// --- Sorting ---

type SortField =
  | "serviceUserName"
  | "userName"
  | "timestamp"
  | "reason"
  | "outcome";
type SortDirection = "asc" | "desc";

export const sortRecords = <T extends InstallRecordType>(
  records: T[],
  field: SortField,
  direction: SortDirection
): T[] => {
  const sorted = [...records].sort((a, b) => {
    const valA = (a as Record<string, unknown>)[field] ?? "";
    const valB = (b as Record<string, unknown>)[field] ?? "";

    if (field === "timestamp") {
      return new Date(String(valA)).getTime() - new Date(String(valB)).getTime();
    }

    return String(valA).toLowerCase().localeCompare(String(valB).toLowerCase());
  });

  return direction === "desc" ? sorted.reverse() : sorted;
};

// --- Badge colors ---

interface BadgeStyle {
  backgroundColor: string;
  color: string;
}

const REASON_STYLES: Record<string, BadgeStyle> = {
  refused: { backgroundColor: theme.risk2, color: theme.risk6 },
  unavailable: { backgroundColor: theme.unexpected1, color: theme.neutral7 },
  unsafe: { backgroundColor: theme.risk1, color: theme.risk5 },
  logistics: { backgroundColor: theme.primary1, color: theme.primary6 },
  logisticsprocess: { backgroundColor: theme.primary1, color: theme.primary6 },
  technicalissue: { backgroundColor: theme.secondary1, color: theme.secondary3 },
  clientunaware: { backgroundColor: theme.support2, color: theme.neutral7 },
};

const UNKNOWN_STYLE: BadgeStyle = {
  backgroundColor: theme.neutral2,
  color: theme.neutral6,
};

export const getReasonBadgeStyle = (reason: string | null): BadgeStyle => {
  if (!reason) return UNKNOWN_STYLE;
  return REASON_STYLES[reason.toLowerCase()] || UNKNOWN_STYLE;
};

// The API returns some reasons as compound words — map them to readable labels.
const REASON_LABELS: Record<string, string> = {
  technicalissue: "Technical issue",
  logisticsprocess: "Logistics process",
  clientunaware: "Client unaware",
};

export const getReasonLabel = (reason: string | null): string => {
  if (!reason) return "Unknown";
  const mapped = REASON_LABELS[reason.toLowerCase()];
  if (mapped) return mapped;
  return reason.charAt(0).toUpperCase() + reason.slice(1).toLowerCase();
};

// --- Outcome badges ---

const OUTCOME_STYLES: Record<InstallOutcome, BadgeStyle> = {
  succeeded: { backgroundColor: theme.expected1, color: theme.neutral7 },
  failed: { backgroundColor: theme.risk1, color: theme.risk5 },
};

const OUTCOME_LABELS: Record<InstallOutcome, string> = {
  succeeded: "Successful",
  failed: "Unsuccessful",
};

export const getOutcomeBadgeStyle = (outcome: InstallOutcome): BadgeStyle =>
  OUTCOME_STYLES[outcome];

export const getOutcomeLabel = (outcome: InstallOutcome): string =>
  OUTCOME_LABELS[outcome];

// Pseudo value used in the Reason filter to isolate successful installs,
// which never have a real `reason` (it's only set on failed records).
export const SUCCESSFUL_REASON_VALUE = "successful";

// --- Install status badges ---
//
// A resident's *current* installation status lives on the serviceUsers Redux
// state (serviceUserProperties.installationStatus), not in the install-summary
// API response. Values: "complete" | "started" | "failed"; missing means the
// install hasn't begun ("incomplete", per src/Services/Constants/filters.js).

const INSTALL_STATUS_STYLES: Record<string, BadgeStyle> = {
  complete: { backgroundColor: theme.expected1, color: theme.neutral7 },
  started: { backgroundColor: theme.unexpected1, color: theme.neutral7 },
  failed: { backgroundColor: theme.risk1, color: theme.risk5 },
  incomplete: { backgroundColor: theme.neutral2, color: theme.neutral6 },
};

const INSTALL_STATUS_LABELS: Record<string, string> = {
  complete: "Complete",
  started: "Started",
  failed: "Failed",
  incomplete: "Awaiting install",
};

export const getInstallStatusBadgeStyle = (status: string): BadgeStyle =>
  INSTALL_STATUS_STYLES[status] ?? UNKNOWN_STYLE;

export const getInstallStatusLabel = (status: string): string =>
  INSTALL_STATUS_LABELS[status] ??
  status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

// --- Safe date formatting ---

export const safeFormatISO = (isoString: string, formatStr: string): string => {
  try {
    const date = parseISO(isoString);
    if (isNaN(date.getTime())) return isoString;
    return format(date, formatStr);
  } catch {
    return isoString;
  }
};

// --- Chart bucketing ---
//
// The API buckets `periods` by calendar boundaries that don't always match the
// selected granularity (e.g. "Last 4 weeks" at month granularity returns
// clipped calendar-month periods). Instead of trusting the API's bucketing, we
// flatten every record from periods[].records and re-bucket them client-side
// by their `timestamp`, using the organisation's timezone from metadata.

export interface ChartBucket {
  key: string;
  label: string;
  started: number;
  referred: number;
  failed: number;
  succeeded: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_BUCKETS = 400;

// True when a daily bucket key ("YYYY-MM-DD") falls on a Saturday or Sunday.
// Parsed as UTC midnight so the result is independent of the viewer's
// timezone (bucket keys are already org-timezone calendar dates).
export const isWeekendBucketKey = (key: string): boolean => {
  const day = new Date(`${key}T00:00:00Z`).getUTCDay();
  return day === 0 || day === 6;
};

// Manual label arrays so labels are derived with UTC getters, avoiding shifts
// from the viewer's local timezone.
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Converts a UTC timestamp to the calendar date it falls on in the given
// timezone, represented as a UTC-midnight Date so UTC getters are safe to use
// for keys and labels. Falls back to the browser's local timezone if the org
// timezone is missing or invalid.
const getZonedCalendarDate = (iso: string, timeZone?: string): Date | null => {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return null;

  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(date);
      const get = (type: string) =>
        Number(parts.find((p) => p.type === type)?.value);
      const y = get("year");
      const m = get("month");
      const d = get("day");
      if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
        return new Date(Date.UTC(y, m - 1, d));
      }
    } catch {
      // Invalid timezone identifier — fall through to local
    }
  }

  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
};

// Weeks run Monday–Sunday: snap a calendar date back to its Monday.
const mondayOf = (d: Date): Date =>
  new Date(d.getTime() - ((d.getUTCDay() + 6) % 7) * DAY_MS);

const dayKey = (d: Date): string => d.toISOString().slice(0, 10); // yyyy-MM-dd
const monthKey = (d: Date): string => d.toISOString().slice(0, 7); // yyyy-MM

const dayLabel = (d: Date): string =>
  `${WEEKDAY_LABELS[d.getUTCDay()]} ${d.getUTCDate()}`;
const weekLabel = (monday: Date): string =>
  `w/c ${monday.getUTCDate()} ${MONTH_LABELS[monday.getUTCMonth()]}`;
const monthLabel = (d: Date): string =>
  `${MONTH_LABELS[d.getUTCMonth()]} '${String(d.getUTCFullYear()).slice(-2)}`;

const bucketKeyFor = (d: Date, granularity: Granularity): string => {
  switch (granularity) {
    case "W":
      return dayKey(mondayOf(d));
    case "M":
      return monthKey(d);
    case "D":
    default:
      return dayKey(d);
  }
};

type RecordCategory = "started" | "referred" | "failed" | "succeeded";
const RECORD_CATEGORIES: RecordCategory[] = [
  // "started", // TO DO: revert when 'started' values are updated to 'abandoned'
  "referred",
  "failed",
  "succeeded",
];

export const buildChartBuckets = (
  periods: InstallPeriodType[],
  granularity: Granularity,
  range?: { start: string; end: string },
  timezone?: string
): ChartBucket[] => {
  if (!periods || periods.length === 0) return [];

  // Establish the calendar range to enumerate buckets over. Prefer the
  // explicit metadata range; fall back to the span of the returned periods.
  const rangeStartIso = range?.start ?? periods[0]?.period?.start;
  const rangeEndIso = range?.end ?? periods[periods.length - 1]?.period?.end;
  if (!rangeStartIso || !rangeEndIso) return [];

  const startDate = getZonedCalendarDate(rangeStartIso, timezone);
  const endDate = getZonedCalendarDate(rangeEndIso, timezone);
  if (!startDate || !endDate || startDate.getTime() > endDate.getTime()) {
    return [];
  }

  // Enumerate every bucket in the range (including empty ones) so the x-axis
  // is continuous.
  const buckets: ChartBucket[] = [];
  const bucketIndex = new Map<string, ChartBucket>();
  const pushBucket = (key: string, label: string) => {
    const bucket: ChartBucket = {
      key,
      label,
      started: 0,
      referred: 0,
      failed: 0,
      succeeded: 0,
    };
    buckets.push(bucket);
    bucketIndex.set(key, bucket);
  };

  if (granularity === "D") {
    for (
      let t = startDate.getTime();
      t <= endDate.getTime() && buckets.length < MAX_BUCKETS;
      t += DAY_MS
    ) {
      const d = new Date(t);
      pushBucket(dayKey(d), dayLabel(d));
    }
  } else if (granularity === "W") {
    // First bucket is the full Mon–Sun week containing the range start.
    const firstMonday = mondayOf(startDate);
    const lastMonday = mondayOf(endDate);
    for (
      let t = firstMonday.getTime();
      t <= lastMonday.getTime() && buckets.length < MAX_BUCKETS;
      t += 7 * DAY_MS
    ) {
      const monday = new Date(t);
      pushBucket(dayKey(monday), weekLabel(monday));
    }
  } else {
    let year = startDate.getUTCFullYear();
    let month = startDate.getUTCMonth();
    const endYear = endDate.getUTCFullYear();
    const endMonth = endDate.getUTCMonth();
    while (
      (year < endYear || (year === endYear && month <= endMonth)) &&
      buckets.length < MAX_BUCKETS
    ) {
      const d = new Date(Date.UTC(year, month, 1));
      pushBucket(monthKey(d), monthLabel(d));
      month += 1;
      if (month > 11) {
        month = 0;
        year += 1;
      }
    }
  }

  // Count every record from every period into its bucket by timestamp.
  periods.forEach((period) => {
    RECORD_CATEGORIES.forEach((category) => {
      const records = period.records?.[category] ?? [];
      records.forEach((record) => {
        const d = getZonedCalendarDate(record.timestamp, timezone);
        if (!d) return;
        const bucket = bucketIndex.get(bucketKeyFor(d, granularity));
        if (bucket) bucket[category] += 1;
      });
    });
  });

  return buckets;
};

// --- Range label formatting ---
 
export const formatRangeLabel = (
  range: { start: string; end: string },
  granularity: Granularity
): string => {
  // In date-fns format strings a single quote starts a literal, so an
  // escaped quote ('') is needed to render the apostrophe in e.g. "Aug '26".
  switch (granularity) {
    case "D":
      return `${safeFormatISO(range.start, "d MMM ''yy")} – ${safeFormatISO(range.end, "d MMM ''yy")}`;
    case "W":
      return `${safeFormatISO(range.start, "d MMM ''yy")} – ${safeFormatISO(range.end, "d MMM ''yy")}`;
    case "M":
      return `${safeFormatISO(range.start, "d MMM ''yy")} – ${safeFormatISO(range.end, "d MMM ''yy")}`;
    default:
      return `${safeFormatISO(range.start, "d MMM ''yy")} – ${safeFormatISO(range.end, "d MMM ''yy")}`;
  }
};
